import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { useAnalytics, EVENTS, GA4 } from "../hooks/useAnalytics";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Star, Check, ArrowRight, X, Lock, Loader2 } from "lucide-react";
import { SEOHead } from "../components/SEOHead";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { trackEvent, trackGA4 } = useAnalytics();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    trackEvent(EVENTS.PRICING_VISIT);
  }, [trackEvent]);

  const freeFeatures = t("pricing.freeFeatures", { returnObjects: true });
  const proFeatures = t("pricing.proFeatures", { returnObjects: true });

  const handleUpgrade = async () => {
    trackEvent(EVENTS.UPGRADE_PREMIUM, { source: "pricing_premium" });
    trackGA4(GA4.CLICK_START_PREMIUM, { source: "pricing_page" });
    if (!user) {
      sessionStorage.setItem("pending_checkout", "premium");
      navigate(lp("/auth?mode=register&redirect=/pricing&intent=checkout"));
      return;
    }
    if (user.plan === "pro") return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/stripe/create-checkout-session`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      trackGA4(GA4.CHECKOUT_STARTED, { source: "pricing_page" });
      window.location.href = res.data.url;
    } catch {
      toast.error(t("billing.checkoutError"));
      setCheckoutLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-background" data-testid="pricing-page">
      <SEOHead
        title={t("pricing.pageTitle")}
        description={t("pricing.subtitle")}
      />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-4 font-medium">
            {t("pricing.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-4">
            {t("pricing.pageTitle")}
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            {t("pricing.subtitle")}
          </p>
          <p className="text-sm text-teal-700 font-medium mt-3">
            {t("pricing.valueStatement")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free */}
          <Card className="border-slate-200 shadow-sm" data-testid="pricing-card-free">
            <CardContent className="p-8 md:p-10">
              <h3 className="text-xl font-semibold text-slate-900 font-heading mb-1">{t("pricing.freeName")}</h3>
              <p className="text-sm text-slate-500 mb-6">{t("pricing.freeDesc")}</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-slate-900 font-heading">$0</span>
                <span className="text-base text-slate-500 ml-2">/{t("pricing.month")}</span>
              </div>
              <ul className="space-y-3 mb-10">
                {Array.isArray(freeFeatures) && freeFeatures.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                    {f.included ? (
                      <Check className="h-4 w-4 text-teal-500 flex-shrink-0" />
                    ) : (
                      <X className="h-4 w-4 text-slate-300 flex-shrink-0" />
                    )}
                    <span className={f.included ? "" : "text-slate-400"}>{f.name}</span>
                    {!f.included && <Lock className="h-3 w-3 text-slate-300 ml-auto" />}
                  </li>
                ))}
              </ul>
              <Link to={lp("/auth?mode=register")}>
                <Button variant="outline" className="w-full h-12 font-medium text-base" data-testid="pricing-free-btn"
                  onClick={() => { trackEvent(EVENTS.START_FREE_GUIDE, { source: "pricing_free" }); trackGA4(GA4.CLICK_START_FREE, { source: "pricing_page" }); }}>
                  {t("pricing.freeBtn")}
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Premium */}
          <Card className="border-teal-300 shadow-lg relative" data-testid="pricing-card-pro">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-teal-700 text-white hover:bg-teal-700 font-medium px-4 py-1">
                {t("pricing.popular")}
              </Badge>
            </div>
            <CardContent className="p-8 md:p-10">
              <h3 className="text-xl font-semibold text-slate-900 font-heading mb-1">{t("pricing.proName")}</h3>
              <p className="text-sm text-slate-500 mb-6">{t("pricing.proDesc")}</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-slate-900 font-heading">$15</span>
                <span className="text-base text-slate-500 ml-2">/{t("pricing.month")}</span>
              </div>
              <ul className="space-y-3 mb-10">
                {Array.isArray(proFeatures) && proFeatures.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                    <Star className="h-4 w-4 text-teal-500 flex-shrink-0" /> {f.name}
                  </li>
                ))}
              </ul>
              {user?.plan === "pro" ? (
                <Button disabled className="w-full h-12 font-medium text-base" data-testid="pricing-current-plan-btn">
                  {t("billing.currentPlan")}
                </Button>
              ) : (
                <Button
                  className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white font-medium text-base"
                  data-testid="pricing-pro-btn"
                  disabled={checkoutLoading}
                  onClick={handleUpgrade}
                >
                  {checkoutLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("billing.redirecting")}</>
                  ) : (
                    <>{t("pricing.proBtn")} <ArrowRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-slate-500">
            {t("pricing.footer")}
          </p>
        </div>

        {/* Comparison table */}
        <div className="mt-16 max-w-3xl mx-auto" data-testid="pricing-comparison-detail">
          <h2 className="text-xl font-semibold text-slate-900 text-center mb-6">{t("pricing.compTitle")}</h2>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left p-4 font-semibold text-slate-900">{t("landing.compFeature")}</th>
                  <th className="text-center p-4 font-semibold text-slate-900 w-28">{t("landing.free")}</th>
                  <th className="text-center p-4 font-semibold text-white bg-teal-700 w-28">{t("landing.pro")}</th>
                </tr>
              </thead>
              <tbody>
                {(t("landing.compFeatures", { returnObjects: true }) || []).map((feat, i) => (
                  <tr key={`pcomp-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="p-4 text-slate-600">{feat}</td>
                    <td className="text-center p-4">
                      {i < 3 ? <Check className="h-4 w-4 text-teal-600 mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                    </td>
                    <td className="text-center p-4 bg-teal-50/50">
                      <Check className="h-4 w-4 text-teal-600 mx-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
}
